import { useState } from 'react';
import { useMutation } from 'react-apollo';
import axios from 'axios';

import { assetImagesUpload as assetImagesUploadMutation } from '../graphql/mutations/account';
import { photoUploadFilter } from '../util/photo-upload';
import { IMAGE_SIZE_CODE } from '../constants/image-size-codes';
import { getErrorString } from '../util/normalize-errors';
import { getMaxThumb } from '../util/photo';

/**
 * Custom hook for uploading ImageAssets.
 *
 * @param {Object} options
 * @param {function} options.onImageUploaded Callbacck function on successful upload.
 * @returns {{image: undefined, assetImage: *, onUploadReset: onUploadReset, onUpload: (function(*=): Promise<undefined>), uploading: boolean, uploadError: unknown}}
 */
const useAssetImageUpload = ({ onImageUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadEror] = useState(null);
  const [image, setImage] = useState();
  const [assetImagesUpload] = useMutation(assetImagesUploadMutation);

  const onUpload = async file => {
    if (uploading || !file) {
      return;
    }

    setUploadEror(null);

    if (!photoUploadFilter(file)) {
      setUploadEror('Please provide a JPEG, HEIC or PNG image.');
      return;
    }

    try {
      setUploading(true);

      const imageUploadResponse = await assetImagesUpload({
        variables: {
          input: {
            images: [{ filename: file.name, content_type: file.type }],
          },
        },
      });

      const [
        imageUploadData,
      ] = imageUploadResponse.data.assetImagesUpload.image_uploads;

      await axios.put(imageUploadData.url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      const imagePreviewUrl = URL.createObjectURL(file);

      // Create a faux-Photo object so that we maintain a unified API
      // across existing Photo selections vs custom uploads.
      const fauxImage = {
        id: imageUploadData.id,
        // Assign the URLs to all photo size codes
        // to be safe as some templates render different photo sizes
        // (i.e. Waldograms could show LARGE vs Collages that show MEDIUM).
        thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(size => ({
          size_code: size,
          url: imagePreviewUrl,
          width: 0,
          height: 0,
        })),
        url: imagePreviewUrl,
        content_type: file.type,
        original_filename: file.name,
        is_faux_image: true,
      };

      setImage(fauxImage);
      onImageUploaded(fauxImage, {
        id: imageUploadData.id,
        url: imagePreviewUrl,
        content_type: file.type,
      });
    } catch (error) {
      setUploadEror(getErrorString(uploadError));
    } finally {
      setUploading(false);
    }
  };

  const onUploadReset = () => {
    setImage(null);
  };

  return {
    onUpload,
    onUploadReset,
    uploadError,
    uploading,
    image,
    assetImage: image
      ? {
          id: image?.id,
          url: getMaxThumb(image?.thumbnail_urls)?.url,
        }
      : undefined,
  };
};

export default useAssetImageUpload;
